import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Lock, Check, X, ArrowRight } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import * as asuntosApi from '@/api/asuntos.api';
import { fechaCorta } from '@/lib/athena';

/**
 * LO QUE ATHENA TE PREGUNTA ANTES DE GUARDARLO.
 *
 * Esteban, 20-sep-2026: *"si es algo personal me tiene que notificar si lo
 * guardamos o solamente si lo ignora"*.
 *
 * ═══ QUÉ PASABA ANTES ═══
 *
 * Dos cosas, y ninguna era ésta:
 *
 *   1. Al inspector se le decía en el prompt que NO extrajera nada personal.
 *      Lo personal desaparecía y el dueño no se enteraba de que había habido
 *      algo.
 *   2. La columna `cajon` nace con `DEFAULT 'TRABAJO'` y **nadie la escribía
 *      nunca**. Así que el filtro "lo personal no sale" —el candado del
 *      mandato— filtraba un cajón que estaba siempre vacío. Lo que se colaba,
 *      se colaba disfrazado de trabajo.
 *
 * Entre las dos, lo personal o se perdía o entraba mal.
 *
 * ═══ POR QUÉ VA EN "HOY" Y NO EN UNA PESTAÑA NUEVA ═══
 *
 * El mandato fija cinco pestañas. Una sexta para esto sería una pantalla que
 * hay que acordarse de visitar, y una pregunta que nadie ve no es una
 * pregunta. Va arriba de Hoy, que es lo primero que se abre.
 *
 * Si no hay nada que preguntar, no se dibuja nada: una tarjeta vacía que dice
 * "no hay preguntas" ocupa el lugar de los tres segundos.
 */

export function TePregunto() {
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();

  const pendientes = useQuery({
    queryKey: ['te-pregunto'],
    queryFn: asuntosApi.getLoQueTePregunto,
  });

  const decidir = useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: 'guardar' | 'ignorar' }) =>
      asuntosApi.moverAsunto(id, accion),
    onSuccess: (_r, { accion }) => {
      void qc.invalidateQueries({ queryKey: ['te-pregunto'] });
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
      toast.success(
        accion === 'guardar'
          ? 'Guardado en lo personal. No va a salir en Hoy.'
          : 'Listo, no te lo vuelvo a preguntar.',
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const lista = pendientes.data?.asuntos ?? [];
  if (pendientes.isPending || lista.length === 0) return null;

  return (
    <Card className="border-amber-300 dark:border-amber-500/40">
      <CardBody className="space-y-3">
        <div className="flex items-start gap-2">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              {lista.length === 1
                ? 'Vi algo tuyo, no de trabajo'
                : `Vi ${lista.length} cosas tuyas, no de trabajo`}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              No lo guardé en ninguna lista. Tú decides.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {lista.map((a) => (
            <div
              key={a.id}
              className="rounded-lg border border-slate-200 p-2.5 dark:border-slate-700"
            >
              <p className="text-sm text-slate-800 dark:text-slate-100">{a.descripcion}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {a.conQuien} · {fechaCorta(a.fechaCompromiso)}
              </p>

              {/* LA CITA. Sin esto el dueño tiene que confiar en que la máquina
                  entendió bien algo íntimo, que es justo lo que no va a hacer. */}
              {a.citaTextual && (
                <p className="mt-1.5 border-l-2 border-slate-300 pl-2 text-xs italic text-slate-500 dark:border-slate-600 dark:text-slate-400">
                  «{a.citaTextual}»
                </p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <button
                  onClick={() => decidir.mutate({ id: a.id, accion: 'guardar' })}
                  disabled={decidir.isPending}
                  className="flex min-h-[36px] items-center gap-1 rounded-full bg-brand-600 px-3 text-xs font-medium text-white disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" /> Guárdalo
                </button>
                <button
                  onClick={() => decidir.mutate({ id: a.id, accion: 'ignorar' })}
                  disabled={decidir.isPending}
                  className="flex min-h-[36px] items-center gap-1 rounded-full border border-slate-300 px-3 text-xs text-slate-600 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                >
                  <X className="h-3.5 w-3.5" /> Ignóralo
                </button>
                {a.conversationId && (
                  <button
                    onClick={() =>
                      navegar(
                        `/chats/${a.conversationId}${a.mensajeId ? `?mensaje=${a.mensajeId}` : ''}`,
                      )
                    }
                    className="ml-auto flex min-h-[36px] items-center gap-1 text-xs text-slate-500 dark:text-slate-400"
                  >
                    Ver el mensaje <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* LO QUE "IGNÓRALO" NO HACE.
            Decir sólo "ignorar" se leería como "esto se borra", y no se borra:
            el mensaje llegó por WhatsApp y está en el chat como cualquier otro.
            Prometer de más acá es peor que no ofrecer nada. */}
        <p className="border-t border-slate-200 pt-2 text-[11px] leading-relaxed text-slate-500 dark:border-slate-700 dark:text-slate-400">
          <strong className="font-medium">Ignóralo</strong> quiere decir que no lo anoto y no te lo
          vuelvo a preguntar. El mensaje sigue en tu chat: llegó por WhatsApp y no lo borro de ahí.
          <br />
          <strong className="font-medium">Guárdalo</strong> lo deja en lo personal, que no sale en
          Hoy ni en la búsqueda.
        </p>
      </CardBody>
    </Card>
  );
}
